import React, { useEffect, useState } from 'react';
import { Header } from '../components/Header';

import { TextOnly, Text, LangDictKey } from '../components/Text';
import { useUser } from '../context/User';
import { toast } from 'react-toastify';
import MyReactTable from '../components/ReactTable';
import { DateTime } from '../components/DateTime';
import { getVendorAuditLog } from '../libs/db-lib';
import { formatDateTime2Lines } from '../libs/utils';
import { Loading } from '../components/Loading';

const ActivityLog = () => {
  const { user } = useUser();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [timeFrame, setTimeFrame] = useState<string>('last7Days');
  const [startDate, setStartDate] = useState<string>(
    new DateTime().subtract(7, 'days').startOf('day').toISOString()
  );
  const endDate = new DateTime().endOf('day').toISOString();

  const [search, setSearch] = useState<string>('');

  const [activityLog, setActivityLog] = useState<any[]>([]);

  const timeframeOptions = ['Today', 'last7Days', 'last30Days'].map((key) => {
    return (
      <option key={key} value={key}>
        {TextOnly(key as LangDictKey)}
      </option>
    );
  });

  const handleChangeTimeFrame = (event: any) => {
    event.preventDefault();
    setTimeFrame(event.target.value);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const columns = [
    {
      Header: TextOnly('date'),
      accessor: 'actionDate',
      maxWidth: 70,
      sortingFn: (rowA: any, rowB: any, _columnId: any) => {
        const a = rowA.getValue(_columnId);
        const b = rowB.getValue(_columnId);
        const externalAStr = new Date(a);
        const externalBStr = new Date(b);
        if (externalAStr < externalBStr) {
          return -1;
        } else {
          return 1;
        }
      },
      Cell: ({ row }: any) => {
        const dateObj = formatDateTime2Lines(row.original.actionDate);
        return (
          <div>
            {dateObj.date}
            <br />
            {dateObj.time}
          </div>
        );
      },
    },
    {
      Header: TextOnly('partner'),
      accessor: 'vendorID',
    },
    {
      Header: TextOnly('user'),
      accessor: 'userID',
    },
    {
      Header: TextOnly('userType'),
      accessor: 'userType',
      maxWidth: 90,
    },
    {
      Header: TextOnly('actionCode'),
      accessor: 'actionCode',
    },
    {
      Header: TextOnly('actionMessage'),
      accessor: 'actionMessage',
      minWidth: 150,
    },
  ];

  useEffect(() => {
    switch (timeFrame) {
      case 'last30Days':
        setStartDate(
          new DateTime().subtract(30, 'days').startOf('day').toISOString()
        );
        break;
      case 'Today':
        setStartDate(new DateTime().startOf('day').toISOString());
        break;
      case 'last7Days':
      default:
        setStartDate(
          new DateTime().subtract(7, 'days').startOf('day').toISOString()
        );
        break;
    }
  }, [timeFrame]);

  useEffect(() => {
    const handleGetVendorAuditLog = async () => {
      setIsLoading(true);
      const response = await getVendorAuditLog({
        vendorID: user?.vendorID ?? user?.vendorAutoAuthUUID ?? 'NONE',
        startDate,
        endDate,
      });
      if (!response.error) {
        setActivityLog(
          response.logEntries.sort((a: any, b: any) =>
            b.actionDate.localeCompare(a.actionDate)
          )
        );
      } else {
        toast.error(response?.error, {
          containerId: 'standard',
          autoClose: false,
        });
      }
      setIsLoading(false);
    };

    handleGetVendorAuditLog();
  }, [startDate]);

  return (
    <>
      <Header title={TextOnly('activityLog')} />
      <div className="l-container">
        <div className="l-flex-wrap">
          <div className="c-select">
            <select
              id="timeframe"
              onChange={handleChangeTimeFrame}
              value={timeFrame || TextOnly('last7Days')}
              className="u-text-capitalize"
            >
              {timeframeOptions}
            </select>
          </div>
          <div className="c-field u-margin-bottom-none">
            <label htmlFor="search" className="c-field__label u-is-vishidden">
              <Text tid="search" />
            </label>
            <input
              type="text"
              id="search"
              maxLength={50}
              className="c-input"
              placeholder={TextOnly('search')}
              value={search}
              onChange={handleChange}
            />
            <i className="c-field__input-icon fal fa-search" />
          </div>
        </div>
        <div className="u-margin-top-large">
          {isLoading ? (
            <Loading />
          ) : (
            <MyReactTable
              columns={columns}
              data={activityLog}
              globalFilter={search}
              tableName="ourTools"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ActivityLog;
